

import React, { useState, useEffect } from 'react';
import ExcelTable from '../common/ExcelTable';
import { 
  CustomRowVertical, 
  CustomRowHorizontal, 
  validateShirtCell 
} from '../common/ExcelTableComponents';
import { fetchData, postData } from '../common/Api'
import { useSpace } from '../common/SpaceContextProvider';
import { useHistory } from 'react-router-dom';
import { Tooltip, OverlayTrigger, Form } from 'react-bootstrap';
import InfoIcon from '@mui/icons-material/Info';
import AlertCustom from '../common/AlertCustom';
import CustomDataViewer from '../common/CustomDataViewer';


const TeamsProjectsTab = ({ id }) => {

    const history = useHistory();
    const { teams, projects, shirts, setTeamsProjects } = useSpace();
    const [data, setData] = useState([]);
    const [error, setError] = useState(null);
    const [showDataNotSavedAlert, setShowDataNotSavedAlert] = useState(false);
    const [showDataSavedNotification, setShowDataSavedNotification] = useState(false);
    const [initData, setInitData] = useState([]);
    const [emptyData, setEmptyData] = useState([]);

    const infoTooltip = (
        <Tooltip id="teams-projects-tab-tooltip">
            T-shirt sizing is a project estimation and capacity planning tool that 
            helps you track how much time or effort an initiative will take. To do this, 
            you assign each project or task a t-shirt size—from Extra Small to XXL—to represent 
            that task's relative effort.
        </Tooltip>
    );

    const getTooltip = (value) => {
        
        var tooltip = '';

        const ind = shirts.findIndex(shirt => shirt.name === value);
        const allocated = shirts[ind] !== undefined ? shirts[ind].resource_mos : null;
        const isValid =  validateShirtCell(value, shirts.map(s => s.name));

        if (isValid){
            tooltip = value === '' ? '' :
                 `Resource allocated for a given t-shirt is: ${allocated}`;
        } else {
            tooltip = `Invalid cell value. Allowed values are: ` + shirts.map(s => s.name).join(', ');
        }
        return tooltip;
    }

    const getData = async () => {
        const matrix = Array(teams.length).fill().map(() => 
            Array(projects.length).fill().map(() => (
                { value: "", className: "", tooltip: "" })));
        setEmptyData(JSON.parse(JSON.stringify(matrix)));
        try {
            await fetchData(`/api/v1/spaces/${id}/projectsteams`)
            .then(response => response.data?.length > 0 && response.data?.forEach(element => {
                const rowid = teams.findIndex(obj => obj.id === element.team_id);
                const colid = projects.findIndex(obj => obj.id === element.project_id);
                const tooltipCell = getTooltip(element.value);
                matrix[rowid][colid] = {value: element.value, className: '', tooltip: tooltipCell};
            }));
            setData(matrix);
            setInitData(matrix);
        } catch (error) {
            alert(`Error fetching planning data :: ${error.message}`);
        }
    }

    const saveData = async () => {

        if (JSON.stringify(data) === JSON.stringify(initData) && JSON.stringify(data) !== JSON.stringify(emptyData)){
            alert('No changes to save');
            return;
        }
        const url = `/api/v1/spaces/${id}/projectsteams`;
        const payload = data.map((row, i) => row.map((cell, j) => {
            return cell.value !== '' ? {
                row_id: i, 
                col_id: j,
                team_id: teams[i].id, 
                project_id: projects[j].id, 
                value: cell.value,
                shirt_id: shirts.filter(shirt => 
                    shirt.name === cell.value).map(shirt => 
                        shirt.id
                    )[0]
            } : null
        }));

        const payloadFilt = payload.map(
            item => item.filter(
            element => element !== null )).filter(
                item => item.length > 0);

        try {
            await postData(url, payloadFilt);
        } catch (error) {
            alert(`Error saving data :: ${error.message}`);
        }

        const fetchData = async () => {
            await getData();
        }
        fetchData();
        
        setTeamsProjects(data);
    }

    useEffect(()=> {
        if (error) {
            alert(`Error loading teams :: ${error.message}`);
            if (error.status === 401){
                history.push('/login');
            }
            setError(null);
        }
    }, [error]);

    useEffect(() => {
        const fetchData = async () => {
            await getData();
        }
        if (projects.length > 0 && teams.length > 0 && shirts.length > 0){
            fetchData();
        }
    }, [projects, teams, shirts]);

    useEffect(() => {
        if (data.length > 0 && shirts.length > 0) {
            
            const newData = JSON.parse(JSON.stringify(data));
            newData.map((row, i) => 
                row.map((cell, j) => {
                    const isValid =  validateShirtCell(cell.value, shirts.map(s => s.name));
                    cell.className = isValid || cell.value === '' ? '' : 'invalid-cell';
                    cell.tooltip = getTooltip(cell.value);
                    return cell;
                }));

            if (JSON.stringify(newData) !== JSON.stringify(data)){
                    setData(newData); 
            } 
            setTeamsProjects(newData); 
        }
    }, [data]);


    useEffect(() => {
        if (JSON.stringify(data) !== JSON.stringify(initData)){
            setShowDataNotSavedAlert(true);
        }
    }, [data]);

    const handleRandomAssignment = () => {

        // if data is not empty - show some error

        const newData = JSON.parse(JSON.stringify(emptyData));
        const shirtsNames = shirts.map(item => item.name);

        projects.forEach((p, projectId) => {

            const n = Math.floor(Math.random() * (teams.length-2)) + 1;
            let teamsCopy = JSON.parse(JSON.stringify(teams));
            
            for (let k = 0; k < n; k++) {
                const randomTeamId = Math.floor(Math.random() * teamsCopy.length);
                const randomShirtId = Math.floor(Math.random() * shirts.length);
                const prevData = newData[randomTeamId][projectId];
                newData[randomTeamId][projectId] = {...prevData, value:  shirtsNames[randomShirtId]};
                teamsCopy.splice(randomTeamId, 1);
            }

        })
        setData(newData);
    }

    return (
        <div className='tab-container'>
            <div className='tab-container-main-panel'>
            {
            projects.length > 0 && teams.length > 0 && data.length > 0 ? 
                    <div className="spreadsheet-container-full">    
                        <ExcelTable 
                            data={data}
                            setData={setData}
                            colsItems={projects} 
                            rowsItems={teams}
                            HeaderRow={CustomRowVertical}
                            Row={CustomRowHorizontal}
                            CustomDataViewer={CustomDataViewer}
                            />
                    </div>  : null
            }
            {
                projects.length > 0 && teams.length > 0 ? <>
                    <div className='btn-group-custom'>
                    <button className='btn-custom green' 
                    onClick={() => {
                        saveData();
                        setShowDataSavedNotification(true);              
                    }}>Save</button> 
                    <button className="btn-custom blue" 
                    onClick={()=>{
                        setData(emptyData);
                        setShowDataSavedNotification(false);
                    }}
                    variant="secondary" >Clear All Data</button>
                    </div>

                    <AlertCustom 
                        text={ showDataNotSavedAlert && "Projects-teams shirts assignments not saved"}
                        variant={showDataNotSavedAlert && "danger"}
                        showAlert={showDataNotSavedAlert}
                        setShowAlert={setShowDataNotSavedAlert}
                    /> 

                    <AlertCustom 
                        text={ showDataSavedNotification && "Data saved successfully"}
                        variant={ showDataSavedNotification && "success"}
                        showAlert={showDataSavedNotification}
                        setShowAlert={setShowDataSavedNotification}
                    /> 

                    </> : <div>No teams and projects created.</div>
            }


            <div className='m-2'>
            <p>
                Press <span onClick={() => handleRandomAssignment()} className="underlined-text"
                    >random assignment</span> to get the example. 
            </p>
            </div>

            </div>
            <div className='switch-list-table-view-container'>
                <OverlayTrigger placement="bottom" overlay={infoTooltip}>
                    <div className='icon-container'>
                        <InfoIcon className='switch-view'></InfoIcon>
                    </div>
                </OverlayTrigger>
            </div>
        </div>
    )
}

export default TeamsProjectsTab;